import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { listDistricts, listStates, type DistrictRow, type StateRow } from '../../api/orgApi.js';
import { DataGrid } from '../../components/DataGrid.js';
import { ListShell, StatusBadge, sx } from './orgKit.js';

export function DistrictListPage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<DistrictRow[]>([]);
  const [states, setStates] = useState<StateRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([listDistricts(token), listStates(token)])
      .then(([d, s]) => {
        setRows(d.data);
        setStates(s.data);
      })
      .catch((e) => setError((e as Error).message))
      .finally(() => setLoading(false));
  }, [token]);

  const stateName = (id: string): string => states.find((s) => s.stateId === id)?.stateName ?? '—';

  return (
    <ListShell
      label="district-list"
      title={t('org.district.list.title')}
      newTo="/org/districts/new"
      newLabel={t('org.district.list.new')}
      error={error}
      loading={loading}
      empty={!loading && rows.length === 0}
      emptyText={t('org.district.list.empty')}
    >
      {rows.length > 0 && (
        <DataGrid<DistrictRow>
          caption="districts"
          rows={rows}
          getRowId={(d) => d.districtId}
          rowTestId={(d) => `district-${d.districtId}`}
          searchPlaceholder={t('org.common.searchNameCode')}
          columns={[
            {
              key: 'code',
              header: t('org.col.code'),
              value: (d) => d.districtCode,
              render: (d) => (
                <Link to={`/org/districts/${d.districtId}/edit`} style={sx.link}>{d.districtCode}</Link>
              ),
            },
            { key: 'name', header: t('org.col.name'), value: (d) => d.districtName },
            { key: 'state', header: t('org.col.state'), value: (d) => stateName(d.stateId) },
            {
              key: 'status',
              header: t('org.col.status'),
              value: (d) => d.recordStatusCode,
              render: (d) => <StatusBadge status={d.recordStatusCode} />,
            },
          ]}
        />
      )}
    </ListShell>
  );
}
